import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { Sidebar } from 'components/sidebar/sidebar';
import { CANTEEN_ROLE_LABELS } from 'core/models/canteen.model';
import { ROLE_LABELS } from 'core/models/role.model';
import { CanteenService } from 'core/services/canteen.service';
import { AuthSessionService } from 'core/services/auth-session.service';
import { ProfileService } from 'core/services/profile.service';
import { UserService } from 'core/services/user.service';
import { HSStaticMethods } from 'preline/non-auto';
import { filter } from 'rxjs';
import { Preloader } from 'ui/preloader/preloader';
import { UserAvatar } from 'ui/user-avatar/user-avatar';
import { canteenNav } from './canteen-nav';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, NgClass, Sidebar, UserAvatar, Preloader],
  templateUrl: './app.html',
})
export class App implements OnInit, AfterViewInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly title = inject(Title);
  protected readonly canteen = inject(CanteenService);
  private readonly auth = inject(AuthSessionService);
  private readonly profileService = inject(ProfileService);
  private readonly userService = inject(UserService);

  protected readonly isSidebarOpen = signal(this.shouldSidebarStartOpen());
  protected readonly isUserMenuOpen = signal(false);
  protected readonly noticesOpen = signal(false);
  protected readonly roleLabels = CANTEEN_ROLE_LABELS;
  /** Conta real (OneAuth); fallback só se a sessão ainda não hidratou. */
  protected readonly accountName = computed(
    () => this.auth.user()?.name ?? 'Usuário',
  );
  protected readonly accountRoleLabel = computed(() => {
    const authRole = this.auth.role();
    return authRole ? ROLE_LABELS[authRole] : '';
  });
  /** Visão operacional mock ativa (menus/PDV) - não é a conta OneAuth. */
  protected readonly viewModeLabel = computed(
    () => CANTEEN_ROLE_LABELS[this.canteen.role()],
  );
  protected readonly activeViewProfileId = computed(
    () => this.canteen.profile().id,
  );
  /** Foto do perfil real ou mock. Vazio: iniciais. */
  protected readonly profileAvatarUrl = computed(() => {
    const authUser = this.auth.user();
    if (authUser) {
      const cached = this.userService.getById(authUser.id);
      return this.profileService.avatarUrl(
        authUser.id,
        cached?.avatarUrl ?? authUser.avatarUrl,
      );
    }
    const profile = this.canteen.profile();
    return this.profileService.avatarUrl(profile.id, profile.avatarUrl);
  });
  private readonly url = signal(
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : this.router.url,
  );

  protected readonly navItems = computed(() => canteenNav(this.canteen.role()));
  protected readonly pageTitle = computed(() => this.resolveTitle(this.url()));
  protected readonly isAuthRoute = computed(() => {
    const path = this.url().split('?')[0] || '/';
    return path === '/login' || path === '/auth/callback';
  });
  /** Shell pela sessão OneAuth - não depender do mock Helena para exibir a UI. */
  protected readonly showCanteenShell = computed(
    () => this.auth.isAuthenticated() && !this.isAuthRoute(),
  );

  constructor() {
    effect(() => {
      this.title.setTitle(`${this.pageTitle()} - Cantina`);
    });
  }

  ngOnInit(): void {
    if (this.auth.isAuthenticated()) {
      // Dados da cantina continuam mock; identidade da conta vem do OneAuth.
      if (!this.canteen.signedIn()) {
        this.canteen.signIn();
      }
      if (!this.auth.useMockAuth) {
        this.auth.revalidateFromApi().subscribe({
          next: () => this.profileService.fetchMine().subscribe(),
        });
      }
    }

    this.url.set(this.router.url);
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.url.set(event.urlAfterRedirects);
        setTimeout(() => HSStaticMethods.autoInit(), 100);
      });
  }

  ngAfterViewInit(): void {
    HSStaticMethods.autoInit();
  }

  protected onToggleSidebar(): void {
    this.isSidebarOpen.update((open) => !open);
  }

  protected onCloseSidebar(): void {
    this.isSidebarOpen.set(false);
  }

  protected toggleUserMenu(): void {
    this.noticesOpen.set(false);
    this.isUserMenuOpen.update((open) => !open);
  }

  protected toggleNotices(): void {
    this.isUserMenuOpen.set(false);
    this.noticesOpen.update((open) => !open);
  }

  protected closeMenus(): void {
    this.isUserMenuOpen.set(false);
    this.noticesOpen.set(false);
  }

  /** Troca só a visão operacional mock (menus/regras). Conta OneAuth permanece. */
  protected useViewMode(profileId: string): void {
    this.canteen.signIn(profileId);
    this.closeMenus();
    void this.router.navigateByUrl('/dashboard');
  }

  protected signOut(): void {
    this.closeMenus();
    this.canteen.signOut();
    if (this.auth.isAuthenticated() && !this.auth.useMockAuth) {
      this.auth.logout();
      return;
    }
  }

  @HostListener('document:click', ['$event'])
  protected handleDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    this.closeIfOutside(
      target,
      'canteen-user-menu',
      'canteen-user-menu-toggle',
      () => this.isUserMenuOpen.set(false),
    );
    this.closeIfOutside(
      target,
      'canteen-notices',
      'canteen-notices-toggle',
      () => this.noticesOpen.set(false),
    );
  }

  private closeIfOutside(
    target: HTMLElement,
    menuId: string,
    toggleId: string,
    close: () => void,
  ): void {
    const menu = document.getElementById(menuId);
    const toggle = document.getElementById(toggleId);
    if (menu && toggle && !menu.contains(target) && !toggle.contains(target)) {
      close();
    }
  }

  private resolveTitle(url: string): string {
    const path = url.split('?')[0] ?? '/';
    if (path === '/profile/edit') {
      return 'Editar perfil';
    }
    if (path === '/profile') {
      return 'Meu perfil';
    }
    if (path.startsWith('/orders/')) {
      return 'Pedido';
    }
    if (path.startsWith('/students/')) {
      return 'Conta do aluno';
    }
    if (path === '/products' || path.startsWith('/products/')) {
      return 'Produtos';
    }
    if (path === '/categories' || path.startsWith('/categories/')) {
      return 'Categorias';
    }
    return (
      this.navItems().find((item) => item.route === path)?.label ?? 'Cantina'
    );
  }

  private shouldSidebarStartOpen(): boolean {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return false;
    }
    return window.matchMedia('(min-width: 1024px)').matches;
  }
}
